/* eslint-disable react/prop-types -- TypeScript props enforce shapes; runtime prop-types are not used in this codebase */
/**
 * CalendarView - Monthly Calendar Component
 *
 * PERFORMANCE OPTIMIZATIONS APPLIED:
 * 1. Components extracted to ./CalendarView/ folder for code splitting
 * 2. CalendarEventOrganiser computation lifted to parent via useCalendarEventPositions hook
 * 3. Types and constants moved to separate files
 *
 * See ./CalendarView/README.md for architecture details
 */
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCalendarDay,
  faPlaneDeparture,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  differenceInCalendarWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from 'urql';
import {
  MonthDropdown,
  useCalendarContext,
} from '~/components/layout/CalendarLayout';
import { RescheduleJobDialog } from '~/components/PlannerView/RescheduleJobDialog';
import { CalendarSidebar } from '~/components/ui/CalendarSidebar';
import { IconButton } from '~/components/ui/nucleus/Button/IconButton';
import { JobStatus, RescheduleJobDocument } from '~/gql/graphql';
import { parseJson } from '~/helpers';
import { useLocalStorage } from '~/hooks';
import { useAuth } from '~/hooks/useAuth';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useJobNameSync } from '~/hooks/useJobNameSync';
import { RescheduleDetails } from '../PlannerView/types';

// PERFORMANCE: Modular CalendarView components
// Components and hooks are extracted to ./CalendarView/ for:
// - Better code splitting
// - Lifted CalendarEventOrganiser computation (per-week -> once at parent)
// - Simplified dependency tracking
import {
  getVisibleWeekStarts,
  useCalendarEventPositions,
} from './CalendarView/hooks/useCalendarEventPositions';
import type { Job, PlaceData } from './CalendarView/types';
import { createCellKey, createDraftJobKey } from './CalendarView/utils';
import { VirtualizedWeek } from './CalendarView/VirtualizedWeek';
import { WeekDaysHeader } from './CalendarView/WeekDaysHeader';

// NOTE: CalendarDateCell is no longer defined here - it's in ./CalendarView/CalendarDateCell.tsx
// NOTE: VirtualizedWeek is no longer defined here - it's in ./CalendarView/VirtualizedWeek.tsx
// NOTE: The old inline component definitions have been REMOVED (CalendarDateCell, VirtualizedWeek, etc.)
// Props interface for the monthly calendar component
type CalendarViewProps = {
  placeId: string;
  selectedJobId?: string; // unused in monthly view
  currentMonth: Date;
  onJobClick: (jobId: string) => void; // unused in monthly view (internal handler used)
  onMonthChange?: (month: Date) => void; // Callback to notify parent of month changes
  scrollToTodayRef?: React.MutableRefObject<(() => void) | null>;
  scrollToMonthRef?: React.MutableRefObject<((month: Date) => void) | null>;
};

// Main monthly calendar component
const CalendarView = React.memo<CalendarViewProps>(
  ({
    placeId: propPlaceId,
    currentMonth: propCurrentMonth,
    onMonthChange,
    scrollToTodayRef,
    scrollToMonthRef,
  }) => {
    // Get data from Context
    const context = useCalendarContext();
    const { data = [], stableData, filtersValue, isLoading } = context ?? {};
    const isLoadingEffective = isLoading ?? false;

    const navigate = useNavigate();
    const location = useLocation();

    // Scroll sidebar to the current site from URL, but NOT when navigating
    // between sites within calendar (user just clicked the site, they know where it is)
    type NavState = { fromCalendar?: boolean } | null;
    const locationState = location.state as NavState;
    const isFromCalendar = locationState?.fromCalendar === true;

    // Only scroll to site when coming from outside calendar view
    const focusSiteId = isFromCalendar ? undefined : propPlaceId;
    const [focusRequestToken, setFocusRequestToken] = useState(0);
    useEffect(() => {
      if (!focusSiteId) return;
      setFocusRequestToken((token) => token + 1);
    }, [focusSiteId]);

    useTranslation();
    const { isMobile } = useBreakpoint();
    const storage = useLocalStorage();

    // Extract selected DayView date from URL for passing to cells
    // When DayView is active as a child route, the URL will be /jobs/calendar/:placeId/day?date=YYYY-MM-DD
    // Parent components in React Router v6 see the full pathname including child route segments
    const selectedDayViewDate = useMemo(() => {
      const dateParam = new URLSearchParams(location.search).get('date');

      return dateParam;
    }, [location.search]);

    const [draftCellId, setDraftCellId] = useState<string | null>(null);
    const [highlightedCells, setHighlightedCells] = useState<Set<string>>(
      new Set()
    );

    const [hiddenButtonCell, setHiddenButtonCell] = useState<{
      siteId: string;
      date: string;
    } | null>(null);

    // URL-driven job creation state (parity with PlannerView)
    const urlJobCreation = useMemo(() => {
      if (
        !location.pathname.includes('/jobs/calendar') ||
        !location.pathname.includes('/new')
      ) {
        return null;
      }
      const urlParams = new URLSearchParams(location.search);
      const siteId = urlParams.get('siteId');
      const dateParam = urlParams.get('date');
      if (!siteId || !dateParam) return null;
      return {
        siteId,
        date: dateParam,
        parsedDate: new Date(dateParam),
      } as const;
    }, [location.pathname, location.search]);

    useEffect(() => {
      if (urlJobCreation) {
        const cellId = createCellKey(
          urlJobCreation.siteId,
          urlJobCreation.date
        );
        setDraftCellId(cellId);
        setHighlightedCells(new Set([cellId]));
        // setIsInJobCreationFlow(true);
      } else {
        // Reset all draft/creation state when form is closed or navigated away
        setDraftCellId(null);
        setHighlightedCells(new Set());
        // setIsInJobCreationFlow(false);
        setHiddenButtonCell(null);
      }
    }, [urlJobCreation]);

    // Draft job name from storage
    const draftJobName = useJobNameSync(
      draftCellId
        ? createDraftJobKey(
            draftCellId.split(':')[0] || '',
            draftCellId.split(':')[1] || ''
          )
        : null
    );

    // Get bookings visibility from filter context
    const bookingsHidden = !!filtersValue?.get('bookings');
    // Local override so toolbar toggle works independently but stays in sync with filter bar
    const [bookingsHiddenOverride, setBookingsHiddenOverride] = useState<
      boolean | null
    >(null);
    const bookingsHiddenEffective =
      bookingsHiddenOverride !== null ? bookingsHiddenOverride : bookingsHidden;
    // If filters change via Filter Bar, clear override to resync
    const filtersSignature = useMemo(() => {
      if (!filtersValue) return '';
      const entries = Array.from(filtersValue.entries()).sort(([a], [b]) =>
        a.localeCompare(b)
      );
      return JSON.stringify(entries);
    }, [filtersValue]);
    const hasActiveFilters = useMemo(() => {
      if (!filtersValue) return false;
      for (const [, value] of filtersValue) {
        if (value) return true;
      }
      return false;
    }, [filtersValue]);
    const filtersEffectDidMountRef = useRef(false);
    useEffect(() => {
      if (filtersEffectDidMountRef.current) {
        setBookingsHiddenOverride(null);
      } else {
        filtersEffectDidMountRef.current = true;
      }
    }, [filtersSignature]);

    // GraphQL mutation for rescheduling
    const [, rescheduleJob] = useMutation(RescheduleJobDocument);
    const { isCurrentUser } = useAuth();

    // Handle job reschedule with optimistic update (defined after placeData)
    let handleReschedule: (details: RescheduleDetails) => void =
      useCallback(() => {
        // noop placeholder, reassigned later
      }, []);

    // Use props instead of URL params and internal state
    const placeId = propPlaceId;
    const [visibleMonth, setVisibleMonth] = useState(() =>
      startOfMonth(propCurrentMonth ?? new Date())
    ); // Track currently visible month in viewport
    const parentRef = useRef<HTMLDivElement>(null);

    // Sidebar state management (similar to PlannerView)
    const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(
      !isMobile
    );
    const [isManuallyExpanded, setIsManuallyExpanded] = useState<boolean>(
      !isMobile
    );

    // (removed reveal gate; handled via calendar preservation)

    // Expanded sites state
    const getLocalExpandedSiteIds = () => {
      const localString = storage.get('calendar.expandedSiteIds');
      try {
        const parsed = parseJson(localString);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };

    const setLocalExpandedSiteIds = useCallback(
      (ids: string[]) => {
        storage.set('calendar.expandedSiteIds', JSON.stringify(ids));
      },
      [storage]
    );

    const [expandedSiteIds, setStateExpandedSiteIds] = useState<string[]>(
      () => {
        const saved = getLocalExpandedSiteIds();
        return Array.isArray(saved) ? saved : [];
      }
    );

    const setExpandedSiteIds = useCallback(
      (ids: string[]) => {
        setStateExpandedSiteIds(ids);
        setLocalExpandedSiteIds(ids);
      },
      [setLocalExpandedSiteIds]
    );

    const expandSite = useCallback(
      (siteId: string) => {
        const newExpandedIds = [...expandedSiteIds, siteId];
        setExpandedSiteIds(newExpandedIds);
      },
      [expandedSiteIds, setExpandedSiteIds]
    );

    const collapseSite = useCallback(
      (siteId: string) => {
        const newExpandedIds = expandedSiteIds.filter((id) => id !== siteId);
        setExpandedSiteIds(newExpandedIds);
      },
      [expandedSiteIds, setExpandedSiteIds]
    );

    const isSiteExpanded = useCallback(
      (siteId: string) => expandedSiteIds.includes(siteId),
      [expandedSiteIds]
    );

    const handleSidebarMouseEnter = () => {
      if (isMobile) return;
      if (!isSidebarExpanded) {
        setIsSidebarExpanded(true);
      }
    };

    const handleSidebarMouseLeave = () => {
      if (isMobile) return;
      if (!isManuallyExpanded) {
        setIsSidebarExpanded(false);
      }
    };

    // Calculate site height for sidebar cards
    const siteHeight = useCallback(
      (siteId: string) => {
        const isExpanded = expandedSiteIds.includes(siteId);
        return isExpanded ? 80 : 60; // Simple static heights
      },
      [expandedSiteIds]
    );

    const handleBack = useCallback(() => {
      navigate('/jobs/calendar');
    }, [navigate]);

    const handleJobClick = useCallback(
      (jobId: string, status?: string) => {
        // Preserve original returnTo when navigating from one job to another
        // This ensures closing only requires one click instead of two
        const currentState = location.state as { returnTo?: string } | null;
        const originalReturnTo = currentState?.returnTo;

        // If we're already in a job view, preserve the original returnTo
        // Otherwise, set returnTo to the current calendar path
        const returnTo = originalReturnTo || location.pathname;

        // If the job is a draft (Created), go straight to edit screen
        if (status === JobStatus.Created || status === 'Created') {
          navigate(`${jobId}/edit`, { state: { returnTo } });
          return;
        }
        // Navigate to job detail view
        navigate(`${jobId}`, { state: { returnTo } });
      },
      [navigate, location.pathname, location.state]
    );

    // Find the specific place data from stable (unfiltered) data for site properties
    const stablePlaceData = useMemo(() => {
      if (!stableData?.sites || !placeId) return null;
      const decodedPlaceId = decodeURIComponent(placeId);
      let site = stableData.sites.find(
        (site: { id: string; name: string }) => site.name === decodedPlaceId
      );
      if (!site) {
        site = stableData.sites.find(
          (site: { id: string; name: string }) =>
            site.id === decodedPlaceId || site.id === placeId
        );
      }
      return site || null;
    }, [stableData?.sites, placeId]);

    // Find the corresponding filtered site from the `data` context for calendar jobs
    const filteredPlaceData = useMemo(() => {
      if (!data || !placeId) return null;
      const decodedPlaceId = decodeURIComponent(placeId);
      // The `data` from context is the filtered list of sites
      return (
        data.find(
          (site: { id: string; name: string }) =>
            site.name === decodedPlaceId ||
            site.id === decodedPlaceId ||
            site.id === placeId
        ) || null
      );
    }, [data, placeId]);

    // Combine stable data with filtered calendar
    const placeData = useMemo(() => {
      if (!stablePlaceData) return null;
      const filteredCal = filteredPlaceData?.calendar;
      const stableCal = (
        stablePlaceData as { calendar?: PlaceData['calendar'] }
      ).calendar;
      // Strategy:
      // - If filters are active, use filteredCal (may be empty by design)
      // - If no active filters: prefer filteredCal when it has data; otherwise fall back to stableCal
      const effectiveCalendar = hasActiveFilters
        ? filteredCal ?? []
        : filteredCal && filteredCal.length > 0
        ? filteredCal
        : stableCal ?? [];
      return {
        ...stablePlaceData,
        calendar: effectiveCalendar,
      } as PlaceData;
    }, [stablePlaceData, filteredPlaceData, hasActiveFilters]);

    // Track loading state for add job button
    const [isLoadingJobForm, setIsLoadingJobForm] = useState(false);

    // Reset loading state when form opens (with delay to avoid blinking)
    // Also reset immediately if navigating away from the form route
    useEffect(() => {
      if (!location.pathname.includes('/new')) {
        setIsLoadingJobForm(false);
        return;
      }
      // Wait for form animation/transition to complete before resetting
      const timer = setTimeout(() => {
        setIsLoadingJobForm(false);
      }, 300); // Match typical transition duration
      return () => clearTimeout(timer);
    }, [location.pathname]);

    const handleAddJobClick = useCallback(() => {
      if (!placeData?.id) return;
      setIsLoadingJobForm(true);
      // Prefer the currently open DayView date if any; otherwise
      // use today when it's in the visible month, else the first day of the visible month.
      const today = new Date();
      const targetDate =
        selectedDayViewDate ||
        (isSameMonth(today, visibleMonth)
          ? format(today, 'yyyy-MM-dd')
          : format(visibleMonth, 'yyyy-MM-01'));

      navigate(`new?siteId=${placeData.id}&date=${targetDate}`);
    }, [navigate, placeData?.id, selectedDayViewDate, visibleMonth]);

    // Local optimistic calendar state derived from current place data
    const [localCalendar, setLocalCalendar] = useState<PlaceData['calendar']>(
      () => placeData?.calendar || []
    );
    useEffect(() => {
      const next = placeData?.calendar || [];
      // Cache key per place+month so we can reuse data between hydrations
      let cacheKey: string | null = null;
      try {
        if (placeId) {
          cacheKey = `calendar.cache.${placeId}.${format(
            propCurrentMonth,
            'yyyy-MM'
          )}`;
        }
      } catch {
        // ignore
      }

      const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
      const CACHE_VERSION = '1';
      const MAX_ENTRIES_PER_PLACE = 6; // Keep at most 6 months cached per place

      /**
       * Clean up expired and excess cache entries for a given place.
       * Removes entries older than CACHE_TTL_MS and enforces MAX_ENTRIES_PER_PLACE.
       */
      const cleanupCache = (currentPlaceId: string) => {
        try {
          const prefix = `calendar.cache.${currentPlaceId}.`;
          const now = Date.now();
          const entries: Array<{ key: string; timestamp: number }> = [];
          const keysToRemove: string[] = [];

          // Collect keys first to avoid mutation during iteration
          const allKeys: string[] = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key) allKeys.push(key);
          }

          // Now process only relevant keys
          for (const key of allKeys) {
            if (!key || !key.startsWith(prefix)) continue;

            try {
              const raw = sessionStorage.getItem(key);
              const cached = parseJson(raw);

              // Validate entry structure
              if (
                cached?.version === CACHE_VERSION &&
                typeof cached?.timestamp === 'number'
              ) {
                // Remove expired entries immediately
                if (now - cached.timestamp >= CACHE_TTL_MS) {
                  keysToRemove.push(key);
                } else {
                  entries.push({ key, timestamp: cached.timestamp });
                }
              } else {
                // Remove malformed entries
                keysToRemove.push(key);
              }
            } catch (error) {
              // Remove unparseable entries
              console.error(
                `[CalendarView] Failed to parse cache entry for key "${key}":`,
                error
              );
              keysToRemove.push(key);
            }
          }

          // Batch remove all marked keys
          for (const key of keysToRemove) {
            try {
              sessionStorage.removeItem(key);
            } catch (error) {
              console.error(
                `[CalendarView] Failed to remove cache entry "${key}":`,
                error
              );
            }
          }

          // Enforce MAX_ENTRIES_PER_PLACE by evicting oldest entries
          if (entries.length > MAX_ENTRIES_PER_PLACE) {
            entries.sort((a, b) => a.timestamp - b.timestamp);
            const toRemove = entries.slice(
              0,
              entries.length - MAX_ENTRIES_PER_PLACE
            );
            for (const entry of toRemove) {
              try {
                sessionStorage.removeItem(entry.key);
              } catch (error) {
                console.error(
                  `[CalendarView] Failed to remove old cache entry "${entry.key}":`,
                  error
                );
              }
            }
          }
        } catch (error) {
          console.error(
            `[CalendarView] Cache cleanup failed for place "${currentPlaceId}":`,
            error
          );
        }
      };

      if (next.length > 0) {
        setLocalCalendar(next);
        if (cacheKey && placeId) {
          // Run cleanup before writing new cache entry
          cleanupCache(placeId);

          try {
            sessionStorage.setItem(
              cacheKey,
              JSON.stringify({
                version: CACHE_VERSION,
                timestamp: Date.now(),
                data: next,
              })
            );
          } catch (error) {
            // Log quota exceeded or serialization errors
            console.error(
              `[CalendarView] Failed to write cache entry for key "${cacheKey}":`,
              error
            );
          }
        }
        return;
      }

      // If no filters are active and we have no data, try using cached calendar to avoid flash
      if (!hasActiveFilters && cacheKey) {
        try {
          const raw = sessionStorage.getItem(cacheKey);
          const cached = parseJson(raw);

          // Validate and check expiration
          if (
            cached?.version === CACHE_VERSION &&
            typeof cached?.timestamp === 'number' &&
            Date.now() - cached.timestamp < CACHE_TTL_MS &&
            Array.isArray(cached?.data) &&
            cached.data.length > 0
          ) {
            setLocalCalendar(cached.data as PlaceData['calendar']);
            return;
          } else if (
            cached &&
            Date.now() - (cached.timestamp ?? 0) >= CACHE_TTL_MS
          ) {
            // Remove expired entry
            try {
              sessionStorage.removeItem(cacheKey);
            } catch (removeError) {
              console.error(
                `[CalendarView] Failed to remove expired cache entry "${cacheKey}":`,
                removeError
              );
            }
          }
        } catch (error) {
          // Remove malformed entry
          console.error(
            `[CalendarView] Failed to read cache entry for key "${cacheKey}":`,
            error
          );
          try {
            sessionStorage.removeItem(cacheKey);
          } catch (removeError) {
            console.error(
              `[CalendarView] Failed to remove malformed cache entry "${cacheKey}":`,
              removeError
            );
          }
        }
      }

      // Fallback: set empty
      setLocalCalendar([]);
    }, [placeData?.calendar, hasActiveFilters, placeId, propCurrentMonth]);

    // Define reschedule handler now that localCalendar exists
    // Dialog + reschedule state (parity with PlannerView wrapper)
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [rescheduleDetails, setRescheduleDetails] =
      useState<RescheduleDetails>();
    const [isRescheduling, setIsRescheduling] = useState(false);

    const revertOptimistic = useCallback((details: RescheduleDetails) => {
      setLocalCalendar((prev) => {
        const next = prev.map((job) => {
          if (job.id !== details.jobId) return job;
          const startDate = new Date(job.start);
          const endDate = job.end ? new Date(job.end) : null;
          const newStart = addDays(
            startDate,
            -details.offsetDays
          ).toISOString();
          const newEnd = endDate
            ? addDays(endDate, -details.offsetDays).toISOString()
            : undefined;
          return { ...job, start: newStart, end: newEnd };
        });
        return next;
      });
    }, []);

    const applyOptimistic = useCallback((details: RescheduleDetails) => {
      setLocalCalendar((prev) => {
        const next = prev.map((job) => {
          if (job.id !== details.jobId) return job;
          const startDate = new Date(job.start);
          const endDate = job.end ? new Date(job.end) : null;
          const newStart = addDays(startDate, details.offsetDays).toISOString();
          const newEnd = endDate
            ? addDays(endDate, details.offsetDays).toISOString()
            : undefined;
          return { ...job, start: newStart, end: newEnd };
        });
        return next;
      });
    }, []);

    const handleImmediateReschedule = useCallback(
      (_details: RescheduleDetails) => {
        setIsRescheduling(true);
        rescheduleJob({
          input: { id: _details.jobId, offsetDays: _details.offsetDays },
        })
          .then((result) => {
            if (result.error) {
              revertOptimistic(_details);
            }
          })
          .catch(() => revertOptimistic(_details))
          .finally(() => setIsRescheduling(false));
      },
      [rescheduleJob, revertOptimistic]
    );

    handleReschedule = useCallback(
      (details: RescheduleDetails) => {
        // Optimistic update first
        applyOptimistic(details);

        if (details.jobStatus === JobStatus.Offered) {
          setRescheduleDetails(details);
          setDialogIsOpen(true);
          return;
        }

        if (details.jobStatus === JobStatus.Accepted) {
          // If current user is assignee, immediate; otherwise show dialog
          if (details.assigneeId && isCurrentUser(details.assigneeId)) {
            handleImmediateReschedule(details);
          } else {
            setRescheduleDetails(details);
            setDialogIsOpen(true);
          }
          return;
        }

        // Created jobs: immediate
        handleImmediateReschedule(details);
      },
      [applyOptimistic, handleImmediateReschedule, isCurrentUser]
    );

    // Pre-compute jobs by date map (date filtering now handled in CalendarLayout)
    // Use content-based memoization to avoid recalculating when localCalendar reference changes but content is identical
    const localCalendarHash = useMemo(() => {
      if (!localCalendar || localCalendar.length === 0) return '';

      // Helper to serialize assignee for hash
      const serializeAssignee = (
        assignee?: { id: string; name: string; image?: string | null } | null
      ): string => {
        if (!assignee) return '';
        return [assignee.id, assignee.name, assignee.image || ''].join(',');
      };

      // Helper to serialize tags for hash (tags affect filtering)
      const serializeTags = (tags?: string[] | null): string => {
        if (!tags || tags.length === 0) return '';
        return tags.sort().join(',');
      };

      // Create hash from all fields that affect rendering or filtering:
      // - id, status, dates, times: affect positioning and display
      // - assignee: displayed in pill (avatar) and affects filtering
      // - tags: affects filtered property (filtering logic)
      // - filtered: affects opacity display
      // This prevents unnecessary recalculations when array reference changes but content is same
      return localCalendar
        .map((job) => {
          if (!job.start) return '';
          return [
            job.id,
            job.status,
            job.summary,
            job.start,
            job.end || '',
            job.scheduledStartTime || '',
            job.scheduledEndTime || '',
            serializeAssignee(job.assignee),
            serializeTags(job.tags),
            String(job.filtered ?? false),
          ].join('|');
        })
        .join('::');
    }, [localCalendar]);

    const jobsByDateMap = useMemo(() => {
      const jobMap = new Map<string, Job[]>();

      if (localCalendar) {
        localCalendar.forEach((job) => {
          if (job.start) {
            // Filter out bookings if they are hidden
            if (job.status === 'Booking' && bookingsHiddenEffective) {
              return;
            }

            // Map the job data to match the Job type expected by ScheduleEventPill
            // The filtered property should already be set by CalendarLayout
            const mappedJob: Job & { filtered?: boolean } = {
              id: job.id,
              summary: job.summary,
              status: job.status as JobStatus | 'Booking',
              start: job.start,
              end: job.end ?? undefined,
              scheduledStartTime: job.scheduledStartTime,
              scheduledEndTime: job.scheduledEndTime,
              timeZone: job.timeZone,
              assignee: job.assignee,
              attributes: job.attributes,
              filtered: job.filtered || false, // Use the filtered property from CalendarLayout
            };

            // CRITICAL: Add job to EVERY day it spans (like PlannerView.tsx does)
            const eventStartDate = startOfDay(new Date(job.start));
            const eventEndDate = job.end
              ? startOfDay(new Date(job.end))
              : eventStartDate;

            // For each day this job spans, add it to the corresponding date bucket
            let currentDate = new Date(eventStartDate);
            while (currentDate <= eventEndDate) {
              const dateKey = format(currentDate, 'yyyy-MM-dd');
              if (!jobMap.has(dateKey)) {
                jobMap.set(dateKey, []);
              }
              jobMap.get(dateKey)!.push(mappedJob);
              currentDate = addDays(currentDate, 1);
            }
          }
        });
      }

      return jobMap;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localCalendarHash, bookingsHiddenEffective]); // localCalendarHash changes when localCalendar content changes, so this is sufficient

    // Pre-index jobs by week start (to avoid scanning all jobs per week)
    // Use content-based memoization to avoid recalculating when localCalendar reference changes but content is identical
    const jobsByWeekStart = useMemo(() => {
      const map = new Map<number, PlaceData['calendar']>();
      if (!localCalendar) return map;

      // Build weekly buckets for each job span
      for (const job of localCalendar) {
        if (!job.start) continue;
        if (job.status === 'Booking' && bookingsHiddenEffective) continue;

        const jobStart = startOfDay(new Date(job.start));
        const jobEnd = job.end ? startOfDay(new Date(job.end)) : jobStart;

        // Iterate week by week that the job touches
        let cursor = startOfWeek(jobStart, { weekStartsOn: 1 });
        const lastWeek = startOfWeek(jobEnd, { weekStartsOn: 1 });
        while (cursor <= lastWeek) {
          const key = cursor.getTime();
          if (!map.has(key)) map.set(key, []);
          map.get(key)!.push(job);
          cursor = addDays(cursor, 7);
        }
      }

      return map;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localCalendarHash, bookingsHiddenEffective]); // localCalendarHash changes when localCalendar content changes, so this is sufficient

    // Generate continuous weeks for virtualization (similar to PlannerView approach)
    // Use a stable reference date so weeks array doesn't regenerate on every month change
    const { count, indexOfToday, weeks } = useMemo(() => {
      const today = new Date();
      const base = startOfMonth(today); // Use today as stable base, not visibleMonth
      const startDate = startOfWeek(addMonths(base, -12), { weekStartsOn: 1 });
      const end = startOfWeek(addMonths(base, 12), { weekStartsOn: 1 });
      const totalDays = differenceInCalendarDays(end, startDate) + 7; // Add a week for safety
      const totalWeeks = Math.ceil(totalDays / 7);

      // Generate week start dates
      const weeksArray = [];
      for (let i = 0; i < totalWeeks; i++) {
        weeksArray.push(addDays(startDate, i * 7));
      }

      const idx = Math.floor(differenceInCalendarDays(today, startDate) / 7);
      const clampedIndexOfToday = idx >= 0 && idx < totalWeeks ? idx : -1;
      return {
        count: totalWeeks,
        indexOfToday: clampedIndexOfToday,
        weeks: weeksArray,
      } as const;
    }, []); // Empty deps - generate once based on today, never regenerate

    // Calculate weeks needed for the current month including adjacent days
    const monthDisplayInfo = useMemo(() => {
      // Get the start of the week containing the first day of the month
      const monthStart = startOfWeek(startOfMonth(propCurrentMonth), {
        weekStartsOn: 1,
      });
      // Get the end of the week containing the last day of the month
      const monthEnd = endOfWeek(endOfMonth(propCurrentMonth), {
        weekStartsOn: 1,
      });

      const weeksInMonth =
        differenceInCalendarWeeks(monthEnd, monthStart, { weekStartsOn: 1 }) +
        1;

      return {
        monthStart,
        monthEnd,
        weeksInMonth,
        // Most months need 4-6 weeks, with 5 being most common
        // Always show the complete month grid including adjacent days
        optimalWeeksInView: weeksInMonth,
      };
    }, [propCurrentMonth]);

    // Track if virtualizer is ready for navigation
    const [virtualizerReady, setVirtualizerReady] = useState(false);
    const [hasInitialScrolled, setHasInitialScrolled] = useState(false);
    const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Always render exactly 5 week rows in the available scroll height
    const [rowHeightPx, setRowHeightPx] = useState<number>(200);
    const lastRowHeightRef = useRef<number>(rowHeightPx);
    const lastVirtualizerReadyRef = useRef<boolean>(virtualizerReady);
    useEffect(() => {
      if (rowHeightPx !== lastRowHeightRef.current) {
        lastRowHeightRef.current = rowHeightPx;
      }
    }, [rowHeightPx]);
    useEffect(() => {
      if (virtualizerReady !== lastVirtualizerReadyRef.current) {
        lastVirtualizerReadyRef.current = virtualizerReady;
      }
    }, [virtualizerReady]);

    // Track programmatic navigation to prevent scroll conflicts
    const isProgrammaticNavigationRef = useRef(false);
    const programmaticUntilRef = useRef<number>(0);
    // Track the last prop month we've synced to, to avoid bounce-backs when parent hasn't changed
    const lastPropCurrentMonthRef = useRef<Date | null>(propCurrentMonth);

    // Duration for which programmatic navigation guards user-driven handlers
    const PROGRAMMATIC_NAV_DURATION_MS = 1200;

    // Schedules clearing of programmatic navigation flags, aligning with the timestamp guard
    const scheduleProgrammaticRelease = useCallback(() => {
      if (releaseProgrammaticTimeoutRef.current) {
        clearTimeout(releaseProgrammaticTimeoutRef.current);
        releaseProgrammaticTimeoutRef.current = null;
      }
      const remainingMs = Math.max(
        0,
        programmaticUntilRef.current - Date.now()
      );
      releaseProgrammaticTimeoutRef.current = setTimeout(() => {
        isProgrammaticNavigationRef.current = false;
        programmaticUntilRef.current = 0;
        releaseProgrammaticTimeoutRef.current = null;
      }, remainingMs);
    }, []);

    // Track scheduled timers/animation frames to avoid updates after unmount
    const initialScrollTimeoutRef = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);
    const releaseProgrammaticTimeoutRef = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);
    const fadeInRafRef = useRef<number | null>(null);
    const mountedRef = useRef(true);
    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        if (initialScrollTimeoutRef.current) {
          clearTimeout(initialScrollTimeoutRef.current);
          initialScrollTimeoutRef.current = null;
        }
        if (releaseProgrammaticTimeoutRef.current) {
          clearTimeout(releaseProgrammaticTimeoutRef.current);
          releaseProgrammaticTimeoutRef.current = null;
        }
        if (fadeInRafRef.current !== null) {
          cancelAnimationFrame(fadeInRafRef.current);
          fadeInRafRef.current = null;
        }
      };
    }, []);

    // Let the virtualizer calculate row heights automatically
    // We'll use CSS to make rows fill available space

    // Virtualization setup for weeks with built-in change detection
    const weekVirtualizer = useVirtualizer({
      count,
      getScrollElement: () => parentRef.current,
      // Use the observed scroll height to derive a row height that fits 5 rows exactly
      estimateSize: () => rowHeightPx,
      overscan: 2,
      useAnimationFrameWithResizeObserver: true,
      onChange: (instance) => {
        // Mark as ready when virtualizer has been initialized and measured
        if (
          !virtualizerReady &&
          instance.scrollElement &&
          instance.scrollRect &&
          instance.scrollRect.height > 0
        ) {
          setVirtualizerReady(true);
        }

        // Derive row size from the scroll viewport height (5 rows)
        const containerHeight = instance.scrollRect?.height ?? 0;
        if (containerHeight > 0) {
          const nextRowHeight = Math.max(1, Math.floor(containerHeight / 5));
          if (nextRowHeight !== rowHeightPx) {
            setRowHeightPx(nextRowHeight);
          }
        }
      },
    });

    // The virtualizer handles resize observation automatically via its built-in observers

    // PERFORMANCE OPTIMIZATION: Pre-compute event positions for all visible weeks
    // This lifts CalendarEventOrganiser computation from VirtualizedWeek (per-week)
    // to here (once for all visible weeks), eliminating redundant calculations
    const visibleWeekStarts = useMemo(() => {
      const virtualItems = weekVirtualizer.getVirtualItems();
      return getVisibleWeekStarts(weeks, virtualItems, 2);
    }, [weekVirtualizer, weeks]);

    const weekEventPositionsMap = useCalendarEventPositions({
      calendar: localCalendar,
      siteId: placeData?.id,
      visibleWeekStarts,
      bookingsHidden: bookingsHiddenEffective,
    });

    // Fallback mechanism: Force virtualizer ready state after timeout if not already ready
    // This is especially important on mobile where measurements might be delayed
    useEffect(() => {
      // Clear any existing timer
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }

      // If already ready or already scrolled, no need for fallback
      if (virtualizerReady || hasInitialScrolled) {
        return;
      }

      // Set a fallback timer to force ready state after 500ms on mobile, 800ms on desktop
      const fallbackDelay = isMobile ? 500 : 800;

      fallbackTimerRef.current = setTimeout(() => {
        if (!virtualizerReady && !hasInitialScrolled) {
          console.warn(
            '[CalendarView] Forcing virtualizer ready via fallback timer',
            {
              isMobile,
              timestamp: Date.now(),
            }
          );
          setVirtualizerReady(true);
        }
      }, fallbackDelay);

      return () => {
        if (fallbackTimerRef.current) {
          clearTimeout(fallbackTimerRef.current);
          fallbackTimerRef.current = null;
        }
      };
    }, [virtualizerReady, hasInitialScrolled, isMobile]);

    // Re-measure when month info or row height changes
    useEffect(() => {
      try {
        weekVirtualizer.measure();
      } catch {
        // no-op
      }
    }, [monthDisplayInfo.weeksInMonth, rowHeightPx, weekVirtualizer]);

    // Resolve week index for a given month start; fallback if exact match not found
    const getWeekIndexForMonthStart = useCallback(
      (monthStart: Date) => {
        const exactIndex = weeks.findIndex(
          (weekStart) => weekStart.getTime() === monthStart.getTime()
        );
        if (exactIndex !== -1) return exactIndex;
        // Fallback: compute approximate index based on week difference
        try {
          const approx = Math.floor(
            differenceInCalendarWeeks(monthStart, weeks[0], { weekStartsOn: 1 })
          );
          return Math.max(0, Math.min(count - 1, approx));
        } catch {
          return 0;
        }
      },
      [weeks, count]
    );

    // Auto-scroll to target month on initial load - wait for proper container measurement
    useEffect(() => {
      if (
        indexOfToday !== -1 &&
        weekVirtualizer &&
        weeks.length > 0 &&
        virtualizerReady && // Wait for virtualizer to be ready
        rowHeightPx > 0 &&
        !hasInitialScrolled // Only run once
      ) {
        // Use route-provided month - always provided by parent route
        const initialTarget = startOfMonth(propCurrentMonth ?? new Date());

        // Prevent scroll detection from reacting to this programmatic scroll
        isProgrammaticNavigationRef.current = true;
        programmaticUntilRef.current =
          Date.now() + PROGRAMMATIC_NAV_DURATION_MS;
        scheduleProgrammaticRelease();

        // Find the first week that contains the start of the target month
        const monthStart = startOfWeek(startOfMonth(initialTarget), {
          weekStartsOn: 1,
        });

        // Resolve index with fallback safety
        const firstWeekOfMonthIndex = getWeekIndexForMonthStart(monthStart);

        // Scroll to the first week of the target month (instant, no animation)
        weekVirtualizer.scrollToIndex(firstWeekOfMonthIndex, {
          align: 'start',
          behavior: 'auto',
        });

        setVisibleMonth(initialTarget);

        // After initial scroll, perform a verification scroll, then fade in
        requestAnimationFrame(() => {
          // Re-align once more after measurements/row-height settle to avoid drift
          setTimeout(() => {
            const verifyMonthStart = startOfWeek(startOfMonth(initialTarget), {
              weekStartsOn: 1,
            });
            const verifyIndex = getWeekIndexForMonthStart(verifyMonthStart);
            weekVirtualizer.scrollToIndex(verifyIndex, {
              align: 'start',
              behavior: 'auto',
            });
            // Reveal content after verification pass; guard release is scheduled above
            requestAnimationFrame(() => {
              setHasInitialScrolled(true);
            });
          }, 100);
        });
      }
    }, [
      weekVirtualizer,
      indexOfToday,
      weeks,
      virtualizerReady,
      rowHeightPx,
      hasInitialScrolled,
      propCurrentMonth,
      getWeekIndexForMonthStart,
      scheduleProgrammaticRelease,
      isMobile,
    ]);

    // After reload/hot-reload, realign viewport to the tracked visibleMonth if scroll reset
    useEffect(() => {
      if (!virtualizerReady || !hasInitialScrolled) return;
      if (!weekVirtualizer || !weeks.length) return;
      const scrollElement = parentRef.current;
      if (!scrollElement) return;

      const detectionPoint = scrollElement.scrollTop + rowHeightPx * 2;
      const virtualItems = weekVirtualizer.getVirtualItems();

      let targetWeekIndex: number | null = null;
      for (const item of virtualItems) {
        const itemTop = item.start;
        const itemBottom = item.start + item.size;
        if (detectionPoint >= itemTop && detectionPoint < itemBottom) {
          targetWeekIndex = item.index;
          break;
        }
      }

      if (targetWeekIndex === null) {
        const approx = Math.floor(detectionPoint / Math.max(1, rowHeightPx));
        targetWeekIndex = Math.max(0, Math.min(weeks.length - 1, approx));
      }

      const targetWeek = weeks[targetWeekIndex];
      if (!targetWeek) return;

      const detectedMonth = startOfMonth(targetWeek);
      if (isSameMonth(detectedMonth, visibleMonth)) return;

      // Guard scroll detection while we re-sync the viewport to visibleMonth
      isProgrammaticNavigationRef.current = true;
      programmaticUntilRef.current = Date.now() + PROGRAMMATIC_NAV_DURATION_MS;
      scheduleProgrammaticRelease();

      const monthStart = startOfWeek(startOfMonth(visibleMonth), {
        weekStartsOn: 1,
      });
      const weekIndex = getWeekIndexForMonthStart(monthStart);
      weekVirtualizer.scrollToIndex(weekIndex, {
        align: 'start',
        behavior: 'auto',
      });
    }, [
      virtualizerReady,
      hasInitialScrolled,
      weekVirtualizer,
      rowHeightPx,
      weeks,
      visibleMonth,
      getWeekIndexForMonthStart,
      scheduleProgrammaticRelease,
    ]);

    // Removed prop-based month synchronization effect to avoid race conditions
    // between user-driven scroll updates and parent-provided month. Initial
    // alignment is handled by the initial-load effect above, and programmatic
    // navigation is performed via the exposed ref methods (navigateToMonth and
    // navigateToToday), both of which guard scroll detection while in flight.

    // Removed prop-based month sync: we only auto-scroll on initial load and
    // on explicit user navigation via navigateToMonth / Today button.

    // Simple scroll detection for month updates
    useEffect(() => {
      const el = parentRef.current;
      if (!el) return;
      if (!hasInitialScrolled) return; // Don't start scroll detection until initial load is complete

      let timeoutId: ReturnType<typeof setTimeout>;

      const updateVisibleMonth = () => {
        // Ignore scroll-driven month updates during programmatic navigation
        if (isProgrammaticNavigationRef.current) return;
        // We always allow local month update and notifying parent.
        // Parent handlers guard against redundant updates with isSameMonth.
        const scrollElement = parentRef.current;
        if (!scrollElement) return;

        const scrollTop = scrollElement.scrollTop;

        // Look at the 2nd visible week row for balanced responsiveness
        // Month changes when you've scrolled about 2 weeks into a new month
        const detectionPoint = scrollTop + rowHeightPx * 2;

        let targetWeekIndex: number | null = null;
        const virtualItems = weekVirtualizer.getVirtualItems();
        if (virtualItems.length) {
          // Find the week at the detection point
          for (const item of virtualItems) {
            const itemTop = item.start;
            const itemBottom = item.start + item.size;
            if (detectionPoint >= itemTop && detectionPoint < itemBottom) {
              targetWeekIndex = item.index;
              break;
            }
          }
        }

        // Fallback: estimate index from scrollTop and row height
        if (targetWeekIndex === null) {
          const approx = Math.floor(detectionPoint / Math.max(1, rowHeightPx));
          targetWeekIndex = Math.max(0, Math.min(weeks.length - 1, approx));
        }

        const targetWeek = weeks[targetWeekIndex];
        if (!targetWeek) return;

        // Use the first day (Monday) of the target week to determine month
        // This gives more consistent results than Wednesday
        const dominantMonth = startOfMonth(targetWeek);

        if (!isSameMonth(dominantMonth, visibleMonth)) {
          setVisibleMonth(dominantMonth);
          onMonthChange?.(dominantMonth);
        }
      };

      const onScroll = () => {
        // Only process scroll if not during programmatic navigation
        if (isProgrammaticNavigationRef.current) return;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(updateVisibleMonth, 100); // Fast, responsive updates
      };

      el.addEventListener('scroll', onScroll, { passive: true });
      // Unlock guard on clear user interactions, but do not clear during programmatic window
      const unlock = () => {
        if (Date.now() < programmaticUntilRef.current) {
          // Still within programmatic window: ensure a scheduled release exists and exit early
          scheduleProgrammaticRelease();
          return;
        }
        if (releaseProgrammaticTimeoutRef.current) {
          clearTimeout(releaseProgrammaticTimeoutRef.current);
          releaseProgrammaticTimeoutRef.current = null;
        }
        isProgrammaticNavigationRef.current = false;
        programmaticUntilRef.current = 0;
      };
      el.addEventListener('wheel', unlock, {
        passive: true,
      } as EventListenerOptions);
      el.addEventListener(
        'touchstart',
        unlock as EventListener,
        { passive: true } as EventListenerOptions
      );
      el.addEventListener(
        'mousedown',
        unlock as EventListener,
        {
          passive: true,
        } as EventListenerOptions
      );

      return () => {
        el.removeEventListener('scroll', onScroll);
        el.removeEventListener('wheel', unlock as EventListener);
        el.removeEventListener('touchstart', unlock as EventListener);
        el.removeEventListener('mousedown', unlock as EventListener);
        clearTimeout(timeoutId);
      };
    }, [
      weeks,
      visibleMonth,
      onMonthChange,
      weekVirtualizer,
      rowHeightPx,
      scheduleProgrammaticRelease,
      hasInitialScrolled,
    ]);

    // Enhanced navigation to position month start at top of viewport
    const navigateToMonth = useCallback(
      (targetMonth: Date) => {
        // Set flag to prevent scroll detection from interfering
        isProgrammaticNavigationRef.current = true;
        programmaticUntilRef.current =
          Date.now() + PROGRAMMATIC_NAV_DURATION_MS;
        scheduleProgrammaticRelease();

        // Find the first week of the target month
        const monthStart = startOfWeek(startOfMonth(targetMonth), {
          weekStartsOn: 1,
        });

        const firstWeekIndex = getWeekIndexForMonthStart(monthStart);

        if (firstWeekIndex !== -1) {
          // Scroll to the first week of the month and align it to the top
          weekVirtualizer.scrollToIndex(firstWeekIndex, {
            align: 'start',
            behavior: 'smooth',
          });
        } else {
          console.warn(
            '[CalendarView] Could not find week index for month start!'
          );
        }

        // Update visible month and notify parent
        setVisibleMonth(targetMonth);
        // Assume parent will catch up; avoid bounce-back
        lastPropCurrentMonthRef.current = targetMonth;
        onMonthChange?.(targetMonth);

        // Guard release is scheduled based on PROGRAMMATIC_NAV_DURATION_MS
      },
      [
        weekVirtualizer,
        onMonthChange,
        getWeekIndexForMonthStart,
        scheduleProgrammaticRelease,
      ]
    );

    const navigateToToday = useCallback(() => {
      const today = new Date();
      navigateToMonth(today); // Just scroll to today's month
    }, [navigateToMonth]);

    // Set up refs for parent component to control scrolling - MUST be before conditional returns
    useEffect(() => {
      if (scrollToTodayRef) {
        scrollToTodayRef.current = navigateToToday;
      }
      if (scrollToMonthRef) {
        scrollToMonthRef.current = navigateToMonth;
      }
    }, [scrollToTodayRef, scrollToMonthRef, navigateToToday, navigateToMonth]);

    // If route params not ready yet, avoid error flash; render lightweight placeholder
    if (!placeId) {
      return <div className='h-full w-full bg-white' />;
    }

    if (!placeData && !isLoadingEffective) {
      return (
        <div className='flex h-full flex-col'>
          {/* Header with back button */}
          <div className='flex items-center justify-between border-b border-gray-200 bg-white p-4'>
            <div className='flex items-center gap-3'>
              <h1 className='text-xl font-semibold text-gray-900'>
                Monthly View
              </h1>
            </div>
          </div>

          {/* Error state */}
          <div className='flex flex-1 items-center justify-center p-8'>
            <div className='text-center'>
              <h2 className='mb-4 text-2xl font-bold text-gray-900'>
                Place not found
              </h2>
              <p className='mb-4 text-gray-600'>
                Could not find place with ID: {placeId}
              </p>
              <button
                onClick={handleBack}
                className='bg-blue-600 hover:bg-blue-700 rounded-md px-4 py-2 text-white transition-colors'
              >
                Back to Calendar
              </button>
            </div>
          </div>
        </div>
      );
    }

    const sites = data || []; // Use filtered data instead of stableData

    const showTodayButton = !isSameMonth(visibleMonth, new Date());

    // If still loading and no placeData, render empty container
    if (isLoadingEffective && !placeData) {
      return <div className='h-full w-full bg-gray-100' />;
    }

    return (
      <>
        <div
          data-usetiful='calendar-view-grid'
          className={classNames(
            'relative flex h-full min-h-0 bg-gray-100',
            hasInitialScrolled
              ? 'visible opacity-100 transition-opacity duration-150'
              : 'invisible opacity-0'
          )}
        >
          {/* Unified responsive toolbar: bookings toggle, month dropdown, today */}
          <div
            className={classNames(
              'fixed z-50 flex flex-col',
              // Mobile placement just above typical FAB
              'bottom-28 left-[60px] right-0',
              // Desktop/tablet: center over main content area
              'justify-center md:absolute md:bottom-5 md:left-0 md:right-0 md:z-50 md:pl-[170px] md:pt-0 lg:bottom-8'
            )}
          >
            <div
              className='z-30 mx-auto flex h-fit flex-row items-center gap-1 rounded-full bg-white/95 px-2 py-2 text-secondary shadow-md ring-1 ring-outline-variant md:relative md:bottom-auto md:left-auto md:right-auto md:flex lg:translate-x-0'
              role='toolbar'
              aria-label='Monthly view options'
              data-usetiful='calendar-view-toolbar'
            >
              <IconButton
                ariaLabel={
                  bookingsHiddenEffective ? 'Show bookings' : 'Hide bookings'
                }
                onClick={() => {
                  setBookingsHiddenOverride(!bookingsHiddenEffective);
                }}
                toggled={!bookingsHiddenEffective}
                tooltip={
                  bookingsHiddenEffective ? 'Show bookings' : 'Hide bookings'
                }
                size='md'
                variant={!bookingsHiddenEffective ? 'outlined' : 'default'}
                icon={<FontAwesomeIcon icon={faPlaneDeparture as IconProp} />}
              />

              <div className='mx-1 block h-5 w-px bg-outline-variant' />

              <div className='block'>
                <MonthDropdown
                  currentDate={visibleMonth}
                  onMonthSelect={(date) => navigateToMonth(date)}
                  openUpwards={true}
                  shortMonth={isMobile}
                  openLeftwards={isMobile}
                  variant='toolbar'
                />
              </div>

              {showTodayButton && (
                <>
                  <div className='mx-1 hidden h-5 w-px bg-outline-variant md:block' />
                  <button
                    type='button'
                    aria-label='Go to today'
                    tabIndex={0}
                    className={classNames(
                      'pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full bg-white text-secondary shadow-md transition-all duration-200 md:w-auto',
                      'hover:bg-surface-container-low hover:text-primary',
                      'focus:outline-1 focus:outline-outline-variant active:text-primary active:ring-1 active:ring-primary-container'
                    )}
                    style={{
                      paddingLeft: '12px',
                      paddingRight: '12px',
                    }}
                    onClick={() => navigateToToday()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigateToToday();
                      }
                    }}
                    title='Today'
                  >
                    <FontAwesomeIcon
                      className='md:hidden'
                      icon={faCalendarDay as IconProp}
                    />

                    <span className='hidden text-sm font-medium md:block'>
                      Today
                    </span>
                  </button>
                </>
              )}
              <div className='mx-1 block h-5 w-px bg-outline-variant lg:hidden' />
              <div className='lg:hidden'>
                <IconButton
                  ariaLabel='Add job'
                  tooltip='Add job'
                  onClick={handleAddJobClick}
                  disabled={isLoadingJobForm}
                  size='md'
                  variant='primary'
                  icon={
                    isLoadingJobForm ? (
                      <svg
                        className='h-6 w-6 animate-spin'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        ></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                      </svg>
                    ) : (
                      <FontAwesomeIcon icon={faPlus as IconProp} />
                    )
                  }
                />
              </div>
            </div>
          </div>

          {/* Using default drag preview (no custom drag layer) */}
          {/* Sidebar - using shared CalendarSidebar component */}
          <CalendarSidebar
            sites={sites}
            currentSiteId={placeData?.id}
            isSidebarExpanded={isSidebarExpanded}
            setIsSidebarExpanded={setIsSidebarExpanded}
            isManuallyExpanded={isManuallyExpanded}
            setIsManuallyExpanded={setIsManuallyExpanded}
            expandedSiteIds={expandedSiteIds}
            onToggleSiteExpansion={(siteId) => {
              if (isSiteExpanded(siteId)) {
                collapseSite(siteId);
              } else {
                expandSite(siteId);
              }
            }}
            onSidebarMouseEnter={handleSidebarMouseEnter}
            onSidebarMouseLeave={handleSidebarMouseLeave}
            siteHeight={siteHeight}
            navigationPath='/jobs/calendar'
            viewType='calendar'
            variant='calendar'
            showDimming={true}
            focusSiteId={focusSiteId}
            focusRequestToken={focusRequestToken}
            onSiteNavigate={(site, isCurrent) => {
              if (isCurrent) {
                // Navigate to planner with targetSiteId so it scrolls to this site
                navigate('/jobs/planner', {
                  state: { targetSiteId: site.id },
                });
                return;
              }
              // Navigate to calendar for the selected site
              // Pass fromCalendar flag to prevent sidebar auto-scroll (user clicked the site)
              navigate(`/jobs/calendar/${encodeURIComponent(site.id)}`, {
                state: { fromCalendar: true },
              });
            }}
          />

          {/* Main Content */}
          <div
            className={classNames(
              'flex min-h-0 flex-1 flex-col',
              isMobile ? 'ml-[66px]' : ''
            )}
          >
            {/* Sticky weekday headers */}
            <div className='sticky top-0 z-10 h-14 border-b border-gray-300 bg-white'>
              <WeekDaysHeader />
            </div>

            {/* Virtualized calendar content */}
            <div
              ref={parentRef}
              className='min-h-0 flex-1 overflow-auto bg-white'
            >
              <div
                style={{
                  height: `${weekVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {weekVirtualizer.getVirtualItems().map((virtualItem) => {
                  const weekStartDate = weeks[virtualItem.index];
                  const weekKey = startOfWeek(weekStartDate, {
                    weekStartsOn: 1,
                  }).getTime();
                  const weekCalendarJobs = jobsByWeekStart.get(weekKey) || [];

                  return (
                    <div
                      key={virtualItem.key.toString()}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      <VirtualizedWeek
                        weekStartDate={weekStartDate}
                        placeData={placeData!}
                        weekCalendarJobs={weekCalendarJobs}
                        onJobClick={handleJobClick}
                        onReschedule={handleReschedule}
                        navigate={navigate}
                        storage={storage}
                        currentMonth={visibleMonth}
                        rowHeight={virtualItem.size}
                        jobsByDate={jobsByDateMap}
                        bookingsHidden={bookingsHiddenEffective}
                        draftCellId={draftCellId}
                        highlightedCells={highlightedCells}
                        setDraftCellId={setDraftCellId}
                        setHighlightedCells={setHighlightedCells}
                        urlJobCreation={urlJobCreation}
                        draftJobName={draftJobName || null}
                        hiddenButtonCell={hiddenButtonCell}
                        setHiddenButtonCell={setHiddenButtonCell}
                        setIsInJobCreationFlow={() => {}}
                        selectedDayViewDate={selectedDayViewDate}
                        // PERFORMANCE: Pre-computed event positions from parent
                        precomputedPositions={weekEventPositionsMap.get(
                          weekKey
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Dialog parity with PlannerView wrapper */}
        <RescheduleJobDialog
          isOpen={dialogIsOpen}
          onClose={() => {
            // Revert optimistic update when user clicks "Go back"
            if (rescheduleDetails) {
              revertOptimistic(rescheduleDetails);
            }
            setDialogIsOpen(false);
            setRescheduleDetails(undefined);
            setIsRescheduling(false);
          }}
          isLoading={isRescheduling}
          onSubmit={(_, { comment }) => {
            if (!rescheduleDetails) return;
            setIsRescheduling(true);
            rescheduleJob({
              input: {
                id: rescheduleDetails.jobId,
                offsetDays: rescheduleDetails.offsetDays,
                comment: comment || undefined,
                // notify: notify || undefined, // enable when backend supports
              },
            })
              .then((result) => {
                if (result.error) {
                  revertOptimistic(rescheduleDetails);
                } else {
                  setDialogIsOpen(false);
                  setRescheduleDetails(undefined);
                }
              })
              .catch(() => {
                revertOptimistic(rescheduleDetails);
              })
              .finally(() => setIsRescheduling(false));
          }}
          jobName={rescheduleDetails?.jobName ?? ''}
          assigneeName={rescheduleDetails?.assigneeName ?? ''}
          action={
            rescheduleDetails?.jobStatus === JobStatus.Offered
              ? 'offer'
              : 'confirm'
          }
          originalDate={rescheduleDetails?.originalDate}
          targetDate={rescheduleDetails?.targetDate}
        />
      </>
    );
  }
);

CalendarView.displayName = 'CalendarView';

export { CalendarView };
export default CalendarView;
